import { useEffect, useMemo, useState } from 'react'
import type { RoomSnapshot } from '../../lib/api/rooms'
import type { RoomPlayerRow } from '../../lib/database.types'
import { submitSingleCourse } from '../../lib/api/tv'
import { getCardById } from '../../games/ideatthat/data/ingredientCards'
import { IngredientCardImage } from '../../games/ideatthat/components/IngredientCardImage'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'

interface Props {
  snapshot: RoomSnapshot
  me: RoomPlayerRow
}

const COURSE_NAMES = ['Starter', 'Main', 'Dessert'] as const
const COURSE_EMOJIS = ['🥗', '🍽️', '🍮']
const COURSE_HINTS = [
  'Start with something intriguing. Set the tone.',
  'The heart of the meal. Make it a statement.',
  'Finish strong. Sweet, savoury, or utterly unhinged.',
]
const MAX_CHARS = 280

/**
 * Writing phase on the phone. Shows the caller's 9-card hand, a textarea
 * for the active course, and a submit button. After submit, flips to a
 * "dish sent — look up at the TV" waiting state until everyone else is in
 * and the TV advances to presenting.
 */
export function JoinBuildingPhase({ snapshot, me }: Props) {
  const t = useT()
  const { room, submissions, players } = snapshot
  const courseIdx = (room.active_course - 1) as 0 | 1 | 2
  const courseName = t(COURSE_NAMES[courseIdx])
  const courseEmoji = COURSE_EMOJIS[courseIdx]
  const courseHint = t(COURSE_HINTS[courseIdx])

  // My in-progress text & selected cards for THIS course. Reset whenever
  // the course changes so state from course 1 doesn't bleed into course 2.
  const [text, setText] = useState('')
  const [usedCards, setUsedCards] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setText('')
    setUsedCards([])
    setError(null)
  }, [room.active_course])

  const mySub = submissions.find(s => s.player_id === me.id) ?? null
  const mySubmittedText = !mySub ? '' :
    courseIdx === 0 ? mySub.course1_text :
    courseIdx === 1 ? mySub.course2_text :
                      mySub.course3_text
  const iSubmitted = Boolean(mySubmittedText && mySubmittedText.trim())

  // Count who else is done so the waiting screen can show progress.
  // Skipped seats never submit, so count against the active set only —
  // otherwise the "X/N done" total is inflated and never completes.
  const activePlayers = useMemo(() => players.filter(p => !p.skipped), [players])
  const submittedCount = useMemo(() => activePlayers.filter(p => {
    const s = submissions.find(x => x.player_id === p.id)
    if (!s) return false
    const t = courseIdx === 0 ? s.course1_text : courseIdx === 1 ? s.course2_text : s.course3_text
    return Boolean(t && t.trim())
  }).length, [activePlayers, submissions, courseIdx])

  const cards = me.hand.map(id => getCardById(id)).filter(Boolean)

  function toggleCard(id: string) {
    setUsedCards(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    )
  }

  async function handleSubmit() {
    if (busy || !text.trim() || usedCards.length === 0) return
    setBusy(true); setError(null)
    try {
      await submitSingleCourse(
        room.id,
        (courseIdx + 1) as 1 | 2 | 3,
        text.trim(),
        usedCards,
      )
      // The realtime tick will re-derive `iSubmitted = true` and flip us
      // into the waiting view. Keep busy until that tick lands.
    } catch (e) {
      setError(readError(e))
      setBusy(false)
    }
  }

  // ── Waiting-after-submit view ────────────────────────────────────────
  if (iSubmitted) {
    return (
      <div className="flex-1 flex flex-col px-5 py-8 text-center">
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="w-24 h-24 rounded-3xl bg-iet-green/15 flex items-center justify-center
                          text-5xl mb-6">
            {courseEmoji}
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-iet-green mb-1">
            {t('{course} sent to the kitchen', { course: courseName })}
          </p>
          <h1 className="font-display text-3xl font-bold text-brand-text mb-3">{t('Look up at the TV')}</h1>
          <p className="text-brand-muted max-w-xs mb-8">
            {t("You'll present your {course} aloud when it's your turn.", { course: t(COURSE_NAMES[courseIdx].toLowerCase()) })}
          </p>
          <p className="font-display text-5xl font-bold text-brand-primary">
            {submittedCount}/{activePlayers.length}
          </p>
          <p className="text-sm text-brand-muted mt-1">{t('players done')}</p>

          <div className="card p-4 mt-8 text-left max-w-sm w-full">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted mb-2">
              {t('Your {course}', { course: t(COURSE_NAMES[courseIdx].toLowerCase()) })}
            </p>
            <p className="text-brand-text">{mySubmittedText}</p>
          </div>
        </div>
      </div>
    )
  }

  // ── Writing view ─────────────────────────────────────────────────────
  return (
    <div className="flex-1 flex flex-col px-5 py-5 gap-4 pb-32">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted mb-1">
          {t('Course {n} of 3', { n: room.active_course })}
        </p>
        <h1 className="font-display text-3xl font-bold text-brand-text">
          {courseEmoji} {courseName}
        </h1>
        <p className="text-sm text-brand-muted mt-0.5">{courseHint}</p>
      </div>

      {/* Hand of 9 ingredient cards */}
      <div>
        <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">{t('Your ingredients: tap the ones you used')}</p>
        <div className="flex flex-wrap gap-3">
          {cards.map(c => c && (
            <IngredientCardImage
              key={c.id}
              id={c.id}
              size="md"
              selected={usedCards.includes(c.id)}
              onSelect={toggleCard}
            />
          ))}
        </div>
      </div>

      {/* Text */}
      <div>
        <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">{t('Your story')}</p>
        <div className="relative">
          <textarea
            value={text}
            onChange={e => setText(e.target.value.slice(0, MAX_CHARS))}
            placeholder={t('Describe your {course}. Be creative, be weird, be delicious!', { course: t(COURSE_NAMES[courseIdx].toLowerCase()) })}
            rows={6}
            className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                       text-brand-text placeholder:text-brand-muted/60 resize-none
                       focus:outline-hidden focus:border-brand-primary focus:ring-2
                       focus:ring-brand-primary/20 transition-all text-base"
          />
          <div className={`absolute bottom-3 right-4 text-xs font-medium
              ${text.length > MAX_CHARS * 0.9 ? 'text-brand-danger' : 'text-brand-muted'}`}>
            {text.length}/{MAX_CHARS}
          </div>
        </div>
      </div>

      {error && (
        <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
          <p className="text-sm text-brand-danger">{error}</p>
        </div>
      )}

      <div className="floating-actions">
        {usedCards.length === 0 && (
          <p className="text-center text-xs font-semibold text-brand-danger mb-2">{t('Tap at least one ingredient card you used')}</p>
        )}
        <button
          onClick={handleSubmit}
          disabled={busy || !text.trim() || usedCards.length === 0}
          className="btn-primary disabled:opacity-40"
        >
          {busy ? t('Sending…') : t('Send {course} to the kitchen →', { course: courseName })}
        </button>
      </div>
    </div>
  )
}
